import {
  ForwardedRef,
  ReactNode,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';

import { DialogContent, DialogType } from '@gasbi/shared/web/components/Dialog';
import { FormFooter } from '@gasbi/shared/web/components/FormFooter';
import { useFormik } from 'formik';
import { Schema } from 'yup';

import { useFormMode } from '@/hooks/useForm';
import { useConfirmContext } from '@/providers/ConfirmDialogProvider';

import Divider from '@mui/joy/Divider';
import Stack from '@mui/joy/Stack';

export type FormWithRefHandle = {
  handleClose: () => void;
};

type FormWithRefProps<T, P> = {
  type?: DialogType;
  onClose?: () => void;
  initialValues?: T;
  handleSubmit?: (formData: T) => void | boolean | Promise<boolean>;
  children: (formik: ReturnType<typeof useFormik>) => ReactNode;
  validationSchema?: Schema | ((params: P) => Schema);
  schemaParams?: P;
  hideCancelButton?: boolean;
  submitButtonTitle?: string;
  hideActions?: boolean;
};

function FormWithRef<T extends Record<string, any>, P>(
  {
    type = DialogType.Create,
    initialValues = {} as T,
    onClose,
    handleSubmit,
    children,
    validationSchema,
    schemaParams,
    hideCancelButton,
    submitButtonTitle,
    hideActions,
  }: FormWithRefProps<T, P>,
  ref: ForwardedRef<FormWithRefHandle>,
) {
  const { confirm } = useConfirmContext();
  const { setFormType, isCreateMode, isPreviewMode } = useFormMode({
    type,
  });

  const [isLoading, setIsLoading] = useState(false);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema:
      typeof validationSchema === 'function'
        ? validationSchema(schemaParams)
        : validationSchema,
    onSubmit: async (formData: T) => {
      setIsLoading(true);

      const isSubmitted = await handleSubmit(formData);
      if (isSubmitted === true || isSubmitted === undefined) {
        onClose();
      }

      setTimeout(() => setIsLoading(false), 500);
    },
  });

  const { submitForm, resetForm, dirty: isDirty } = formik;

  const handleClose = useCallback(async () => {
    const closeForm = () => {
      onClose();
      resetForm();
    };
    if (isDirty) {
      confirm(closeForm);
    } else {
      closeForm();
    }
  }, [confirm, isDirty, onClose, resetForm]);

  useImperativeHandle(
    ref,
    () => ({
      handleClose,
    }),
    [handleClose],
  );

  return (
    <>
      <DialogContent dividers>
        <Stack divider={<Divider />} gap={2}>
          {children(formik)}
        </Stack>
      </DialogContent>
      {!hideActions && (
        <FormFooter
          isCreateMode={isCreateMode}
          isPreviewMode={isPreviewMode}
          isLoading={isLoading}
          setFormType={setFormType}
          handleCancel={!hideCancelButton && handleClose}
          submitForm={submitForm}
          textSubmit={submitButtonTitle}
        />
      )}
    </>
  );
}

export const ForwardedRefForm = forwardRef(FormWithRef);
